import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { MagnifyingGlass, List, X, Plus, CaretDown, BookOpen, Scales, ChartLineUp, Warning, ShieldCheck, Brain, DeviceMobile, Ticket, TextAa, Lightbulb, Calculator, Buildings, GraduationCap, Trophy, Star, Target, Strategy, Coins, Gift } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const LEARN_GROUPS = [
  {
    heading: "Getting Started",
    items: [
      { to: "/guides", label: "Betting Guides", desc: "Beginner-friendly how-tos", icon: GraduationCap, testid: "nav-guides" },
      { to: "/betting-basics", label: "Betting Basics", desc: "How odds & bet slips work", icon: BookOpen, testid: "nav-basics-hub" },
      { to: "/choosing-a-tipster", label: "Choosing a Tipster", desc: "Pick one worth paying for", icon: Scales, testid: "nav-choosing-hub" },
      { to: "/how-to-evaluate-a-tipster", label: "How to Evaluate a Tipster", desc: "ROI, yield & strike rate", icon: ChartLineUp, testid: "nav-evaluate-hub" },
    ],
  },
  {
    heading: "Win & Compare",
    items: [
      { to: "/tipster-reviews", label: "Reviews & Comparisons", desc: "Judge & compare tipsters", icon: Star, testid: "nav-reviews-hub" },
      { to: "/community/top-reviewers", label: "Top Reviewers", desc: "Community leaderboard & badges", icon: Trophy, testid: "nav-top-reviewers" },
      { to: "/beat-the-bookmaker", label: "Beat the Bookmaker", desc: "Best odds, CLV & exchanges", icon: Target, testid: "nav-beatbookie-hub" },
      { to: "/sport-betting-strategy", label: "Sport Betting Strategy", desc: "Football, racing, tennis, golf", icon: Strategy, testid: "nav-sportstrategy-hub" },
      { to: "/bankroll-and-staking", label: "Bankroll & Staking", desc: "Bet sizing & Kelly", icon: Coins, testid: "nav-bankroll-hub" },
      { to: "/betting-tools", label: "Betting Calculators", desc: "Odds, each-way, Kelly & more", icon: Calculator, testid: "nav-betting-tools" },
    ],
  },
  {
    heading: "Bet Safely",
    items: [
      { to: "/scam-hub", label: "Tipster Scam Hub", desc: "Spot fakes before you pay", icon: Warning, testid: "nav-scam-hub" },
      { to: "/betting-safety", label: "Betting Safety & Law", desc: "UK legality, tax & GAMSTOP", icon: ShieldCheck, testid: "nav-safety-hub" },
      { to: "/betting-psychology", label: "Betting Psychology", desc: "Stay disciplined & in control", icon: Brain, testid: "nav-psychology-hub" },
      { to: "/tipster-platforms", label: "Tipster Platforms", desc: "Telegram, Instagram & more", icon: DeviceMobile, testid: "nav-platforms-hub" },
    ],
  },
  {
    heading: "Reference",
    items: [
      { to: "/bet-types", label: "Bet Types Explained", desc: "Singles, accas, each-way", icon: Ticket, testid: "nav-bet-types" },
      { to: "/betting-terms", label: "Betting Terms Glossary", desc: "Jargon, defined simply", icon: TextAa, testid: "nav-betting-terms" },
      { to: "/betting-strategies", label: "Betting Strategies", desc: "Value, systems & staking", icon: Lightbulb, testid: "nav-betting-strategies" },
      { to: "/betting-metrics", label: "Betting Metrics & Staking", desc: "Bankroll & the numbers", icon: ChartLineUp, testid: "nav-betting-metrics" },
      { to: "/bookmaker-reviews", label: "Bookmaker Reviews", desc: "Where to place your bets", icon: Buildings, testid: "nav-bookmaker-reviews" },
    ],
  },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [hasOffers, setHasOffers] = useState(false);
  useEffect(() => { api.get("/offers").then((r) => setHasOffers(r.data.length > 0)).catch(() => {}); }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/tipsters?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <Logo size="md" />
        </Link>

        <form onSubmit={submitSearch} className="relative ml-4 hidden max-w-md flex-1 md:block">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="navbar-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tipsters..."
            aria-label="Search tipsters"
            className="pl-10"
          />
        </form>

        <nav className="ml-auto hidden items-center gap-1 md:flex">
          <Button variant="ghost" asChild data-testid="nav-browse">
            <Link to="/tipsters">Browse</Link>
          </Button>
          <Button variant="ghost" asChild data-testid="nav-best-tipsters">
            <Link to="/best-tipsters">Best Tipsters</Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" data-testid="nav-learn">Learn <CaretDown size={14} className="ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[880px] p-4">
              <div className="grid grid-cols-4 gap-x-3 gap-y-1">
                {LEARN_GROUPS.map((group) => (
                  <div key={group.heading}>
                    <p className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{group.heading}</p>
                    {group.items.map((it) => {
                      const Icon = it.icon;
                      return (
                        <DropdownMenuItem key={it.to} asChild data-testid={it.testid} className="cursor-pointer">
                          <Link to={it.to} className="flex items-start gap-2.5 rounded-md px-2 py-1.5">
                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                              <Icon size={15} weight="fill" />
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium leading-tight">{it.label}</span>
                              <span className="block truncate text-xs text-muted-foreground">{it.desc}</span>
                            </span>
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </div>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          {hasOffers && (
            <Button variant="ghost" asChild data-testid="nav-offers">
              <Link to="/offers" aria-label="Offers" title="Current tipster offers"><Gift size={16} className="text-amber-500 2xl:mr-1" weight="fill" /><span className="hidden 2xl:inline">Offers</span></Link>
            </Button>
          )}
          <Button variant="ghost" asChild data-testid="nav-blog">
            <Link to="/blog">Blog</Link>
          </Button>
          <Button variant="ghost" asChild data-testid="nav-contact">
            <Link to="/contact">Contact</Link>
          </Button>
          <Button variant="ghost" asChild data-testid="nav-submit">
            <Link to="/submit-tipster"><Plus size={16} className="mr-1" />Add Tipster</Link>
          </Button>
          <ThemeToggle />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu-trigger" className="rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback>{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium">{user.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                {user.role === "admin" && (
                  <DropdownMenuItem asChild data-testid="nav-admin">
                    <Link to="/admin">Admin Dashboard</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild data-testid="nav-my-shortlist">
                  <Link to="/my-shortlist">My Shortlist</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-my-reviews">
                  <Link to="/my-reviews">My Reviews</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}>
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" asChild data-testid="nav-login">
                <Link to="/login">Log in</Link>
              </Button>
              <Button asChild data-testid="nav-register" className="rounded-full">
                <Link to="/register">Sign up</Link>
              </Button>
            </>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain border-t border-border bg-background px-4 py-4 md:hidden">
          <form onSubmit={submitSearch} className="relative mb-3">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tipsters..." aria-label="Search tipsters" className="pl-10" />
          </form>
          <div className="flex flex-col gap-1">
            <Button variant="ghost" asChild className="justify-start"><Link to="/tipsters" onClick={() => setMobileOpen(false)}>Browse</Link></Button>
            <Button variant="ghost" asChild className="justify-start" data-testid="nav-best-tipsters-mobile"><Link to="/best-tipsters" onClick={() => setMobileOpen(false)}>Best Tipsters</Link></Button>
            {LEARN_GROUPS.map((group) => {
              const expanded = openGroup === group.heading;
              return (
                <div key={group.heading} className="mt-1">
                  <button
                    type="button"
                    onClick={() => setOpenGroup(expanded ? null : group.heading)}
                    aria-expanded={expanded}
                    data-testid={`nav-group-${group.heading.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-semibold transition-colors hover:bg-secondary"
                  >
                    {group.heading}
                    <CaretDown size={15} className={`shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </button>
                  {expanded && (
                    <div className="mb-1 pl-2">
                      {group.items.map((it) => {
                        const Icon = it.icon;
                        return (
                          <Button key={it.to} variant="ghost" asChild className="w-full justify-start" data-testid={`${it.testid}-mobile`}>
                            <Link to={it.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5">
                              <Icon size={16} weight="fill" className="text-primary" />{it.label}
                            </Link>
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="mt-2 flex flex-col gap-1">
            {hasOffers && <Button variant="ghost" asChild className="justify-start" data-testid="nav-offers-mobile"><Link to="/offers" onClick={() => setMobileOpen(false)}>Offers</Link></Button>}
            <Button variant="ghost" asChild className="justify-start"><Link to="/blog" onClick={() => setMobileOpen(false)}>Blog</Link></Button>
            <Button variant="ghost" asChild className="justify-start" data-testid="nav-contact-mobile"><Link to="/contact" onClick={() => setMobileOpen(false)}>Contact</Link></Button>
            <Button variant="ghost" asChild className="justify-start"><Link to="/submit-tipster" onClick={() => setMobileOpen(false)}>Add Tipster</Link></Button>
            {user ? (
              <>
                {user.role === "admin" && <Button variant="ghost" asChild className="justify-start"><Link to="/admin" onClick={() => setMobileOpen(false)}>Admin</Link></Button>}
                <Button variant="ghost" asChild className="justify-start"><Link to="/my-shortlist" onClick={() => setMobileOpen(false)}>My Shortlist</Link></Button>
                <Button variant="ghost" asChild className="justify-start"><Link to="/my-reviews" onClick={() => setMobileOpen(false)}>My Reviews</Link></Button>
                <Button variant="ghost" className="justify-start" onClick={() => { logout(); navigate("/"); setMobileOpen(false); }}>Log out</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" asChild className="justify-start"><Link to="/login" onClick={() => setMobileOpen(false)}>Log in</Link></Button>
                <Button asChild className="justify-start"><Link to="/register" onClick={() => setMobileOpen(false)}>Sign up</Link></Button>
              </>
            )}
          </div>
          </div>
        </div>
      )}
    </header>
  );
}
